/**
 * How the automation (ring R4) works one AI call campaign: whether it may, how
 * often a company is tried, between which dates, who gets the tasks its calls
 * leave, and a button that rings the next company in the queue now.
 *
 * The date pickers work in this browser's zone, while the automation reads its
 * calling window in the settings' zone; when the two differ, a line under the
 * dates names both, so a start picked at 10:00 is not taken for 10:00 in the
 * settings' zone (`zoneLabel`).
 *
 * The fields are part of the campaign's draft and are saved with the page's
 * Save button, like every other card here; the draft, the checks and the patch
 * live in `lib/aiCampaignAutomation.ts`. An alert campaign rings once per
 * defect and never retries (the incident is claimed by the call), so its retry
 * fields are hidden and a line says why.
 *
 * "Call the next one now" works the STORED campaign through every gate the
 * automation has except its own switch, the gap and this campaign's switch
 * (in test mode also the calling window), so it is only offered on a saved
 * campaign with nothing unsaved on the page: pressing it over an unsaved edit
 * would ring on terms the owner cannot see. Managers only, as the server
 * checks. What happened comes back as one note (`lib/aiCampaignCallNext.ts`),
 * worded with the automation tab's reasons, or with this card's own fallback
 * when a reason is new.
 */
import { useState } from "react";
import { Loader2, PhoneOutgoing } from "lucide-react";
import { DateTimePicker } from "@wa/ui";
import { useI18n } from "../../../../i18n/I18nProvider";
import { CAMPAIGN_LIMITS, type CampaignDraft } from "../../../../lib/aiCampaigns";
import { assigneeOptions, datesInOrder, readCount, readMoment, usesRetries } from "../../../../lib/aiCampaignAutomation";
import { callNextAiCampaign, callNextNote, type CallNextNote } from "../../../../lib/aiCampaignCallNext";
import { staffLabel, useStaff } from "../../_shared/staff";
import { viewerTimeZone, zoneLabel } from "../automation/automationView";
import { Btn, StatusPill } from "../../../ui";
import { Field, Panel } from "../../marketing/shared";
import { FieldBox, Note, Pill, SelectPill, ToggleRow } from "../../settings/panel/fields";

export function CampaignAutomationCard({
  draft,
  onChange,
  disabled,
  campaignId,
  canManage,
  dirty,
  onCalled,
  settingsZone = null,
}: {
  draft: CampaignDraft;
  onChange: (next: CampaignDraft) => void;
  disabled?: boolean;
  /** The saved campaign's id; null on a new one, which has nothing to call from. */
  campaignId: string | null;
  canManage: boolean;
  /** The page holds unsaved edits, including ones not valid enough to save. */
  dirty: boolean;
  /** A press may have placed a call or taken companies out of the queue: the page reloads. */
  onCalled: () => void;
  /** The automation's zone (`settings.timezone`), when the page could read it. */
  settingsZone?: string | null;
}) {
  const { t, locale } = useI18n();
  const { staff, byId } = useStaff();
  const [calling, setCalling] = useState(false);
  const [note, setNote] = useState<CallNextNote | null>(null);

  const retries = usesRetries(draft.angle);
  const attemptsInvalid = readCount(draft.maxAttempts, CAMPAIGN_LIMITS.maxAttempts) === null;
  const hoursInvalid = readCount(draft.retryAfterHours, CAMPAIGN_LIMITS.retryAfterHours) === null;
  const datesOk = datesInOrder(readMoment(draft.startsAt) ?? null, readMoment(draft.endsAt) ?? null);
  const digits = (text: string, max: number) => text.replace(/[^\d]/g, "").slice(0, max);
  const browserZone = viewerTimeZone();
  const otherZone = settingsZone ? zoneLabel(settingsZone, browserZone) : null;

  async function callNext() {
    if (!campaignId || calling) return;
    setCalling(true);
    setNote(null);
    try {
      setNote(callNextNote({ reply: await callNextAiCampaign(campaignId) }));
    } catch (error) {
      setNote(callNextNote({ error }));
    } finally {
      setCalling(false);
      onCalled();
    }
  }

  function noteText(shown: CallNextNote) {
    if (shown.kind === "placed") {
      return shown.companyName
        ? t("aiCalls.campaigns.automation.callNext.placed", { company: shown.companyName })
        : t("aiCalls.campaigns.automation.callNext.placedNoName");
    }
    if (shown.kind === "error") return t(`aiCalls.campaigns.automation.callNext.error.${shown.code}`);
    // The automation tab owns the reason wording; a reason newer than its
    // catalogue comes back as the key itself and is said with the code instead.
    const text = t(shown.key);
    return text === shown.key ? t("aiCalls.campaigns.automation.callNext.unknown", { code: shown.code }) : text;
  }

  const blockedHint = dirty
    ? t("aiCalls.campaigns.automation.callNext.dirty")
    : draft.status !== "active"
      ? t("aiCalls.campaigns.automation.callNext.notActive")
      : t("aiCalls.campaigns.automation.callNext.hint");

  return (
    <Panel title={t("aiCalls.campaigns.automation.title")} description={t("aiCalls.campaigns.automation.intro")}>
      <div className="space-y-4">
        <ToggleRow
          label={t("aiCalls.campaigns.automation.autoEnabled")}
          hint={t("aiCalls.campaigns.automation.autoEnabledHint")}
          checked={draft.autoEnabled}
          disabled={disabled}
          onChange={(autoEnabled) => onChange({ ...draft, autoEnabled })}
        />

        {retries ? (
          <div className="grid gap-4 sm:grid-cols-2">
            <Field
              label={t("aiCalls.campaigns.automation.maxAttempts")}
              hint={
                attemptsInvalid
                  ? t("aiCalls.campaigns.basics.dailyCapInvalid", { min: CAMPAIGN_LIMITS.maxAttempts.min, max: CAMPAIGN_LIMITS.maxAttempts.max })
                  : t("aiCalls.campaigns.automation.maxAttemptsHint")
              }
            >
              <Pill
                value={draft.maxAttempts}
                inputMode="numeric"
                disabled={disabled}
                onChange={(text) => onChange({ ...draft, maxAttempts: digits(text, 1) })}
              />
            </Field>
            <Field
              label={t("aiCalls.campaigns.automation.retryAfterHours")}
              hint={
                hoursInvalid
                  ? t("aiCalls.campaigns.basics.dailyCapInvalid", {
                      min: CAMPAIGN_LIMITS.retryAfterHours.min,
                      max: CAMPAIGN_LIMITS.retryAfterHours.max,
                    })
                  : t("aiCalls.campaigns.automation.retryAfterHoursHint")
              }
            >
              <Pill
                value={draft.retryAfterHours}
                inputMode="numeric"
                disabled={disabled}
                onChange={(text) => onChange({ ...draft, retryAfterHours: digits(text, 3) })}
              />
            </Field>
          </div>
        ) : (
          <Note tone="mut">{t("aiCalls.campaigns.automation.alertOnce")}</Note>
        )}

        {/* `FieldBox`: the pickers and the menu are buttons, and a label would
            forward its own clicks to them. */}
        <div className="grid gap-4 sm:grid-cols-2">
          <FieldBox label={t("aiCalls.campaigns.automation.startsAt")}>
            <DateTimePicker
              value={draft.startsAt}
              locale={locale}
              placeholder={t("aiCalls.campaigns.automation.noStart")}
              disabled={disabled}
              onChange={(startsAt) => onChange({ ...draft, startsAt })}
            />
          </FieldBox>
          <FieldBox label={t("aiCalls.campaigns.automation.endsAt")}>
            <DateTimePicker
              value={draft.endsAt}
              locale={locale}
              placeholder={t("aiCalls.campaigns.automation.noEnd")}
              disabled={disabled}
              onChange={(endsAt) => onChange({ ...draft, endsAt })}
            />
          </FieldBox>
        </div>
        {datesOk ? null : <Note tone="warn">{t("aiCalls.campaigns.automation.badDates")}</Note>}
        {otherZone ? (
          <Note tone="info">{t("aiCalls.campaigns.automation.zoneHint", { browser: browserZone, settings: otherZone })}</Note>
        ) : null}

        <FieldBox label={t("aiCalls.campaigns.automation.assignee")} hint={t("aiCalls.campaigns.automation.assigneeHint")}>
          <SelectPill
            value={draft.assigneeId}
            onChange={(assigneeId) => onChange({ ...draft, assigneeId })}
            label={t("aiCalls.campaigns.automation.assignee")}
            disabled={disabled}
          >
            {assigneeOptions(staff, draft.assigneeId).map((option) => (
              <option key={option.value || "creator"} value={option.value}>
                {option.kind === "creator"
                  ? t("aiCalls.campaigns.automation.assigneeCreator")
                  : option.kind === "missing"
                    ? t("aiCalls.campaigns.automation.assigneeMissing")
                    : staffLabel(byId, option.value)}
              </option>
            ))}
          </SelectPill>
        </FieldBox>

        {campaignId && canManage ? (
          <div className="space-y-2 border-t border-[var(--crm-hair)] pt-4">
            <Btn onClick={() => void callNext()} disabled={calling || dirty || draft.status !== "active"}>
              {calling ? <Loader2 className="size-3.5 animate-spin" /> : <PhoneOutgoing className="size-3.5" />}
              {t("aiCalls.campaigns.automation.callNext.button")}
            </Btn>
            <p className="crm-sub text-xs">{blockedHint}</p>
            {note ? (
              <Note tone={note.tone} live>
                <span className="inline-flex flex-wrap items-center gap-2">
                  {noteText(note)}
                  {note.kind === "placed" && note.test ? <StatusPill tone="info">{t("aiCalls.test")}</StatusPill> : null}
                </span>
              </Note>
            ) : null}
          </div>
        ) : null}
      </div>
    </Panel>
  );
}
